import { Box, Grid, LinearProgress, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import {
  TOTAL_SERIES,
  getManufacturerCategories,
  getManufacturerProducts,
  getProductUtilization,
  getUtilizationSeries,
  itemEquipmentBreakdown,
  itemEquipmentBreakdownFetching,
  itemProductUtilization,
  itemUtilizationHistory,
  itemUtilizationHistoryFetching,
  utilizationStats,
} from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { CardStyled } from '../../styled/components';
import { UtilizationChart } from '../tools/UtilizationChart';
import { UtilizationSparkline } from '../tools/UtilizationSparkline';
import { NOT_TRACKED_STATUSES, TRACKED_STATUSES, itemsLink } from '../tools/itemsLink';

const formatPct = (value: number): string => `${value.toFixed(value < 10 && value > 0 ? 1 : 0)}%`;

const cellSx = { py: 1.4, px: 2, fontSize: 13, whiteSpace: 'nowrap' };
const headSx = { ...cellSx, fontSize: 12 };

type ManufacturerEquipmentProps = { manufacturerId: string };

// The tools dashboard's two tables, scoped to one manufacturer: its product
// categories on top, driving the utilization chart beside them, and its
// individual products below. Only categories the manufacturer actually sells
// appear, so the table is empty rather than padded for an unused category.
export const ManufacturerEquipment = ({ manufacturerId }: ManufacturerEquipmentProps): ReactElement => {
  const term = useMarketTerm();
  const dispatch = useDispatch();
  const categoriesFor = useSelector(getManufacturerCategories);
  const productsFor = useSelector(getManufacturerProducts);
  const series = useSelector(getUtilizationSeries);
  const productSeries = useSelector(getProductUtilization);
  const fetching = useSelector(itemEquipmentBreakdownFetching);
  const historyFetching = useSelector(itemUtilizationHistoryFetching);
  const [category, setCategory] = useState(TOTAL_SERIES);

  useEffect(() => {
    dispatch(itemEquipmentBreakdown());
    dispatch(itemUtilizationHistory());
    // Product-grained history is scoped to this manufacturer; running it for
    // the whole organization costs several times as much.
    dispatch(itemProductUtilization({ manufacturerId }));
  }, [dispatch, manufacturerId]);

  const categories = useMemo(() => categoriesFor(manufacturerId), [categoriesFor, manufacturerId]);
  const products = useMemo(() => productsFor(manufacturerId, category), [productsFor, manufacturerId, category]);

  const totals = categories.reduce(
    (running, row) => ({ products: running.products + row.products, items: running.items + row.items, tracked: running.tracked + row.tracked }),
    { products: 0, items: 0, tracked: 0 },
  );
  const totalPct = totals.items ? (totals.tracked / totals.items) * 100 : 0;

  // The chart is this manufacturer's own series, narrowed to the selected
  // category, so it never mixes in another manufacturer's equipment.
  const points = series.byManufacturer[manufacturerId]?.[category] || [];
  const statsFor = (scope: string) => utilizationStats(series.byManufacturer[manufacturerId]?.[scope]);
  const scope = category === TOTAL_SERIES ? undefined : category;

  const rowSx = (name: string) => ({ cursor: 'pointer', ...(category === name && { backgroundColor: 'action.selected' }) });

  return (
    <>
      <CardStyled sx={{ mb: 6, p: 6 }}>
        {fetching && <LinearProgress />}

        <Grid container spacing={6} alignItems="stretch">
          <Grid item xs={12} lg={6}>
            <Box>
              <Typography variant="h6">Product Categories</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {term('Tracked')} now, with the past 12 months for comparison
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headSx}>Category</TableCell>
                    <TableCell sx={headSx} align="right">
                      Products
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      Items
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      {term('Tracked')}
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      {term('Tracked')} %
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      Max %
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      Avg %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!fetching && !categories.length && (
                    <TableRow>
                      <TableCell sx={cellSx} colSpan={7}>
                        No equipment from this manufacturer.
                      </TableCell>
                    </TableRow>
                  )}
                  {categories.map((row) => (
                    <TableRow key={row.category} hover selected={category === row.category} sx={rowSx(row.category)} onClick={() => setCategory(row.category)}>
                      <TableCell sx={cellSx}>
                        <Link component="button" type="button" underline="hover" sx={{ fontSize: 13 }} onClick={() => setCategory(row.category)}>
                          {row.category}
                        </Link>
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        {row.products.toLocaleString()}
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        <Link component={RouterLink} to={itemsLink({ category: row.category, manufacturerId })} underline="hover" color="inherit">
                          {row.items.toLocaleString()}
                        </Link>
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        <Link
                          component={RouterLink}
                          to={itemsLink({ category: row.category, manufacturerId, scanStatus: TRACKED_STATUSES })}
                          underline="hover"
                          color="inherit"
                        >
                          {row.tracked.toLocaleString()}
                        </Link>
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        {formatPct(row.trackedPct)}
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        {statsFor(row.category) ? formatPct(statsFor(row.category)!.max) : '—'}
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        {statsFor(row.category) ? formatPct(statsFor(row.category)!.avg) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!!categories.length && (
                    <TableRow hover selected={category === TOTAL_SERIES} sx={rowSx(TOTAL_SERIES)} onClick={() => setCategory(TOTAL_SERIES)}>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }}>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          sx={{ fontSize: 13, fontWeight: 'fontWeightBold' }}
                          onClick={() => setCategory(TOTAL_SERIES)}
                        >
                          {TOTAL_SERIES}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        {totals.products.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        <Link component={RouterLink} to={itemsLink({ manufacturerId })} underline="hover" color="inherit">
                          {totals.items.toLocaleString()}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        <Link component={RouterLink} to={itemsLink({ manufacturerId, scanStatus: TRACKED_STATUSES })} underline="hover" color="inherit">
                          {totals.tracked.toLocaleString()}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        {formatPct(totalPct)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        {statsFor(TOTAL_SERIES) ? formatPct(statsFor(TOTAL_SERIES)!.max) : '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 'fontWeightBold' }} align="right">
                        {statsFor(TOTAL_SERIES) ? formatPct(statsFor(TOTAL_SERIES)!.avg) : '—'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <UtilizationChart fetching={historyFetching} category={category} points={points} />
          </Grid>
        </Grid>
      </CardStyled>

      <CardStyled sx={{ mb: 6, p: 6 }}>
        <Typography variant="h6">Products</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {category === TOTAL_SERIES ? 'All product categories' : `Product category: ${category}`}
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Product</TableCell>
              <TableCell sx={headSx}>SKU</TableCell>
              <TableCell sx={headSx}>Category</TableCell>
              <TableCell sx={headSx} align="right">
                Items
              </TableCell>
              <TableCell sx={headSx} align="right">
                {term('Tracked')}
              </TableCell>
              <TableCell sx={headSx} align="right">
                {term('Not Tracked')}
              </TableCell>
              <TableCell sx={headSx} align="right">
                {term('Tracked')} %
              </TableCell>
              <TableCell sx={headSx} align="right">
                Max %
              </TableCell>
              <TableCell sx={headSx} align="right">
                Avg %
              </TableCell>
              <TableCell sx={{ ...headSx, width: '25%' }}>Utilization, past 12 months</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!fetching && !products.length && (
              <TableRow>
                <TableCell sx={cellSx} colSpan={10}>
                  No products in this category.
                </TableCell>
              </TableRow>
            )}
            {products.map((row) => {
              const points = productSeries[row.productId] || [];
              const stats = utilizationStats(points);
              return (
                <TableRow key={row.productId} hover>
                  <TableCell sx={cellSx}>
                    <Link component={RouterLink} to={`/tools/products/${row.productId}/details`} underline="hover" sx={{ fontSize: 13 }}>
                      {row.productName}
                    </Link>
                  </TableCell>
                  <TableCell sx={cellSx}>{row.sku}</TableCell>
                  <TableCell sx={cellSx}>{row.category}</TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Link
                      component={RouterLink}
                      to={itemsLink({ category: scope, manufacturerId, productId: row.productId })}
                      underline="hover"
                      color="inherit"
                    >
                      {row.items.toLocaleString()}
                    </Link>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Link
                      component={RouterLink}
                      to={itemsLink({ category: scope, manufacturerId, productId: row.productId, scanStatus: TRACKED_STATUSES })}
                      underline="hover"
                      color="inherit"
                    >
                      {row.tracked.toLocaleString()}
                    </Link>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Link
                      component={RouterLink}
                      to={itemsLink({ category: scope, manufacturerId, productId: row.productId, scanStatus: NOT_TRACKED_STATUSES })}
                      underline="hover"
                      color="inherit"
                    >
                      {row.notTracked.toLocaleString()}
                    </Link>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {formatPct(row.trackedPct)}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {stats ? formatPct(stats.max) : '—'}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {stats ? formatPct(stats.avg) : '—'}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: '25%' }}>
                    <UtilizationSparkline points={points} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardStyled>
    </>
  );
};
